import { useCallback, useContext } from 'react';
import { AbTestingContext } from '@src/contexts/ab-testing-context';
import { CONTROL_VARIATION_NAME } from '@src/helpers/ab-testing.helper';

export const useAbTest = (experimentName: string) => {
  const {
    abTestingEnabled,
    abTestingInitialized,
    initializationError,
    abTestingClient,
  } = useContext(AbTestingContext);

  const isCurrentVariation = useCallback(
    (experimentVariation: string) => {
      if (abTestingInitialized) {
        const currentVariation = abTestingClient.getVariation(
          experimentName,
          {},
        );
        // Assume the control variation if Optimizely doesn't know about the experiment
        // (i.e. it doesn't exist). This way we can develop against experiments before configuring them.
        if (currentVariation == null) {
          return experimentVariation === CONTROL_VARIATION_NAME;
        }

        return experimentVariation === currentVariation;
      } else if (!abTestingEnabled) {
        return experimentVariation === CONTROL_VARIATION_NAME;
      } else {
        return false;
      }
    },
    [abTestingClient, abTestingEnabled, abTestingInitialized, experimentName],
  );

  const activate = useCallback(() => {
    if (abTestingInitialized) {
      abTestingClient.activate(experimentName, {});
    } else if (!initializationError) {
      console.error(
        `Attempted to activate experiment ${experimentName} ` +
          'before client finished initializing. Caller should wait.',
      );
    }
  }, [
    abTestingClient,
    abTestingInitialized,
    experimentName,
    initializationError,
  ]);

  return {
    /** Are A/B tests enabled by feature toggle? */
    enabled: abTestingEnabled,
    /**
     * Given the name of a variation, returns true if the user is currently
     * opted into that variation for the experiment. If A/B tests are disabled
     * by toggle, this will only return true when the variation name is
     * 'CONTROL'. If the A/B testing client is still initializing, this always
     * returns false.
     */
    isCurrentVariation,
    /**
     * Activates the experiment variation for analytics tracking purposes. This
     * should be called once, the first time whenever the user sees or interacts
     * with the part of the UX controlled by the experiment.
     */
    activate,
    /** Has the A/B testing client finished initializing? */
    initialized: abTestingInitialized,
    /** null unless the A/B testing client failed to initialize */
    initializationError,
  };
};
